import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { ArrowRight, SlidersHorizontal, Filter, X, Clock, Award, GraduationCap } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const SORT_OPTIONS = [
    { value: "featured", label: "Featured" },
    { value: "best_sellers", label: "Best Sellers" },
    { value: "price_asc", label: "Price: Low to High" },
    { value: "price_desc", label: "Price: High to Low" },
    { value: "newest", label: "Newest" },
];

function CourseCard({ c, mode, onEnroll }) {
    const price = mode === "offline" ? c.price + (c.offline_surcharge ?? 1500) : c.price;
    const soldOutOffline = mode === "offline" && (c.offline_seats_remaining ?? 1) <= 0;
    return (
        <article className="group bg-white rounded-2xl overflow-hidden shadow-soft border border-gray-100 transition-all duration-300 hover:shadow-brand hover:-translate-y-0.5" data-testid={`course-card-${c.id}`}>
            <Link to={`/courses/${c.id}`} className="block relative aspect-[4/3] overflow-hidden bg-gray-50">
                <img src={c.image_url} alt={c.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <span className="absolute top-3 left-3 px-3 py-1 rounded-full bg-white/90 backdrop-blur text-[11px] font-semibold uppercase tracking-wider text-brand-700">
                    {c.level}
                </span>
            </Link>
            <div className="p-5">
                <Link to={`/courses/${c.id}`} className="block">
                    <h3 className="font-heading text-lg font-semibold text-ink-900 hover:text-brand-600 transition-colors line-clamp-1">{c.title}</h3>
                </Link>
                <p className="text-sm text-ink-700 mt-1.5 line-clamp-2 leading-relaxed">{c.description}</p>
                <div className="mt-3 flex flex-wrap gap-3 text-xs text-ink-700">
                    <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-brand-500" />{c.duration}</span>
                    <span className="inline-flex items-center gap-1.5"><Award className="h-3.5 w-3.5 text-accent-500" />Certificate</span>
                </div>
                <div className="mt-4 flex items-end justify-between gap-3">
                    <div>
                        <div className="text-[11px] uppercase tracking-wider text-ink-400">{mode === "offline" ? "Offline" : "From"}</div>
                        <div className="font-heading text-xl font-semibold text-ink-900">₹{price.toLocaleString("en-IN")}</div>
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <Button
                            size="sm"
                            onClick={() => onEnroll(c, "online")}
                            className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta h-8 px-3 text-xs"
                            data-testid={`enroll-online-${c.id}`}
                        >
                            Enroll Online
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={soldOutOffline}
                            onClick={() => onEnroll(c, "offline")}
                            className="rounded-full border-2 border-accent-400 text-accent-600 hover:bg-accent-50 h-8 px-3 text-xs disabled:opacity-50"
                            data-testid={`enroll-offline-${c.id}`}
                        >
                            {soldOutOffline ? "Sold Out" : "Offline"}
                        </Button>
                    </div>
                </div>
                <Link to={`/courses/${c.id}`} className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-700">
                    View details <ArrowRight className="h-3 w-3" />
                </Link>
            </div>
        </article>
    );
}

function FilterSection({ title, children }) {
    return (
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
            <div className="text-[11px] uppercase tracking-wider text-ink-400 font-semibold mb-3">{title}</div>
            {children}
        </div>
    );
}

function Toggle({ label, checked, onChange, testId }) {
    return (
        <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input
                type="checkbox"
                checked={!!checked}
                onChange={(e) => onChange(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                data-testid={testId}
            />
            <span className="text-ink-900">{label}</span>
        </label>
    );
}

export default function CoursesListPage({ onOpenAuth }) {
    const [params, setParams] = useSearchParams();
    const { addItem } = useCart();
    const [categories, setCategories] = useState([]);
    const [facets, setFacets] = useState(null);
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);

    const categoryId = params.get("category_id") || "";
    const selectedLevels = useMemo(() => params.getAll("level"), [params]);
    const selectedDurations = useMemo(() => params.getAll("duration"), [params]);
    const mode = params.get("mode") || "";
    const priceMin = params.get("price_min") ? Number(params.get("price_min")) : null;
    const priceMax = params.get("price_max") ? Number(params.get("price_max")) : null;
    const bestSellers = params.get("best_sellers") === "1";
    const sort = params.get("sort") || "featured";

    const updateParams = (mut) => {
        const sp = new URLSearchParams(params);
        mut(sp);
        setParams(sp, { replace: true });
    };

    useEffect(() => {
        api.get("/course-categories").then(({ data }) => setCategories(data || [])).catch(() => setCategories([]));
        api.get("/courses/facets").then(({ data }) => setFacets(data)).catch(() => setFacets(null));
    }, []);

    useEffect(() => {
        setLoading(true);
        const sp = new URLSearchParams();
        if (categoryId) sp.set("category_id", categoryId);
        for (const lv of selectedLevels) sp.append("level", lv);
        for (const d of selectedDurations) sp.append("duration", d);
        if (mode) sp.set("mode", mode);
        if (priceMin !== null) sp.set("price_min", String(priceMin));
        if (priceMax !== null) sp.set("price_max", String(priceMax));
        if (bestSellers) sp.set("best_sellers_only", "true");
        sp.set("sort", sort);
        api.get(`/courses?${sp.toString()}`)
            .then(({ data }) => setCourses(data || []))
            .catch(() => setCourses([]))
            .finally(() => setLoading(false));
    }, [categoryId, selectedLevels, selectedDurations, mode, priceMin, priceMax, bestSellers, sort]);

    const toggleMulti = (key, value) => {
        const all = params.getAll(key);
        const next = all.includes(value) ? all.filter((x) => x !== value) : [...all, value];
        updateParams((sp) => {
            sp.delete(key);
            for (const v of next) sp.append(key, v);
        });
    };

    const onEnroll = (c, m = "online") => {
        const surcharge = m === "offline" ? (c.offline_surcharge ?? 1500) : 0;
        addItem({ id: c.id, kind: "course", name: c.title, price: c.price + surcharge, image_url: c.image_url, meta: c.duration, mode: m }, 1);
        toast.success(`Enrolled (${m === "offline" ? "Offline" : "Online"})`, { description: c.title });
    };

    const clearAll = () => setParams(new URLSearchParams(), { replace: true });

    const fMin = facets?.price_min ?? 0;
    const fMax = facets?.price_max ?? 0;

    const activeChips = [];
    if (categoryId) {
        const cat = categories.find((c) => c.id === categoryId);
        activeChips.push({ label: `Category: ${cat?.name || categoryId}`, clear: () => updateParams((sp) => sp.delete("category_id")) });
    }
    selectedLevels.forEach((lv) => activeChips.push({ label: `Level: ${lv}`, clear: () => toggleMulti("level", lv) }));
    selectedDurations.forEach((d) => activeChips.push({ label: `Duration: ${d}`, clear: () => toggleMulti("duration", d) }));
    if (mode) activeChips.push({ label: `Mode: ${mode === "online" ? "Online" : "Offline"}`, clear: () => updateParams((sp) => sp.delete("mode")) });
    if (priceMin !== null) activeChips.push({ label: `Min ₹${priceMin}`, clear: () => updateParams((sp) => sp.delete("price_min")) });
    if (priceMax !== null) activeChips.push({ label: `Max ₹${priceMax}`, clear: () => updateParams((sp) => sp.delete("price_max")) });
    if (bestSellers) activeChips.push({ label: "Best sellers", clear: () => updateParams((sp) => sp.delete("best_sellers")) });

    return (
        <div className="min-h-screen bg-paper">
            <Navbar onOpenAuth={onOpenAuth} />
            <main>
                <section className="relative bg-paper-alt border-b border-gray-100">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
                        <div className="text-[11px] uppercase tracking-wider text-brand-700 font-semibold flex items-center gap-1.5">
                            <GraduationCap className="h-3.5 w-3.5" /> All Courses
                        </div>
                        <h1 className="font-heading text-4xl md:text-5xl font-semibold text-ink-900 mt-2">Explore every path</h1>
                        <p className="mt-3 text-base md:text-lg text-ink-700 max-w-2xl">
                            From gentle foundations to teacher training — filter by level, mode, and category to find what fits your practice.
                        </p>
                    </div>
                </section>

                <section className="py-10 md:py-14">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-6">
                        <aside className="lg:col-span-3 space-y-4" data-testid="courses-filters-sidebar">
                            <div className="flex items-center justify-between">
                                <div className="font-heading text-lg font-semibold inline-flex items-center gap-1.5"><SlidersHorizontal className="h-4 w-4" /> Filters</div>
                                {activeChips.length > 0 && (
                                    <button onClick={clearAll} className="text-xs text-brand-700 hover:underline" data-testid="courses-clear-all">Clear all</button>
                                )}
                            </div>

                            <FilterSection title="Category">
                                <div className="space-y-1.5">
                                    <button
                                        onClick={() => updateParams((sp) => sp.delete("category_id"))}
                                        className={`w-full text-left px-3 py-2 rounded-xl text-sm transition ${!categoryId ? "bg-brand-50 text-brand-700 font-medium" : "hover:bg-paper-alt"}`}
                                        data-testid="filter-category-all"
                                    >
                                        All
                                    </button>
                                    {categories.map((c) => (
                                        <button
                                            key={c.id}
                                            onClick={() => updateParams((sp) => sp.set("category_id", c.id))}
                                            className={`w-full text-left px-3 py-2 rounded-xl text-sm transition ${categoryId === c.id ? "bg-brand-50 text-brand-700 font-medium" : "hover:bg-paper-alt"}`}
                                            data-testid={`filter-category-${c.id}`}
                                        >
                                            {c.name}
                                        </button>
                                    ))}
                                </div>
                            </FilterSection>

                            <FilterSection title="Mode">
                                <div className="flex flex-wrap gap-1.5">
                                    {[
                                        { value: "", label: "All" },
                                        { value: "online", label: "Online" },
                                        { value: "offline", label: "On Campus" },
                                    ].map((m) => (
                                        <button
                                            key={m.value || "all"}
                                            onClick={() => updateParams((sp) => m.value ? sp.set("mode", m.value) : sp.delete("mode"))}
                                            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${(mode || "") === m.value ? "bg-brand-500 border-brand-500 text-white" : "bg-white border-gray-200 hover:border-brand-300"}`}
                                            data-testid={`filter-mode-${m.value || "all"}`}
                                        >
                                            {m.label}
                                        </button>
                                    ))}
                                </div>
                            </FilterSection>

                            {facets?.levels?.length > 0 && (
                                <FilterSection title="Level">
                                    <div className="space-y-1.5">
                                        {facets.levels.map((lv) => (
                                            <Toggle
                                                key={lv}
                                                label={lv}
                                                checked={selectedLevels.includes(lv)}
                                                onChange={() => toggleMulti("level", lv)}
                                                testId={`filter-level-${lv.replace(/\s+/g, "-").toLowerCase()}`}
                                            />
                                        ))}
                                    </div>
                                </FilterSection>
                            )}

                            <FilterSection title="Price">
                                <div className="flex items-center gap-2">
                                    <input
                                        type="number"
                                        placeholder={`Min ₹${fMin}`}
                                        value={priceMin ?? ""}
                                        onChange={(e) => updateParams((sp) => e.target.value === "" ? sp.delete("price_min") : sp.set("price_min", e.target.value))}
                                        min={0}
                                        className="w-1/2 px-3 py-1.5 rounded-xl border border-gray-200 text-sm focus:border-brand-300 outline-none"
                                        data-testid="filter-price-min"
                                    />
                                    <span className="text-ink-400 text-xs">to</span>
                                    <input
                                        type="number"
                                        placeholder={`Max ₹${fMax}`}
                                        value={priceMax ?? ""}
                                        onChange={(e) => updateParams((sp) => e.target.value === "" ? sp.delete("price_max") : sp.set("price_max", e.target.value))}
                                        min={0}
                                        className="w-1/2 px-3 py-1.5 rounded-xl border border-gray-200 text-sm focus:border-brand-300 outline-none"
                                        data-testid="filter-price-max"
                                    />
                                </div>
                                <div className="text-[10px] text-ink-400 mt-2">Range: ₹{fMin.toLocaleString("en-IN")} – ₹{fMax.toLocaleString("en-IN")}</div>
                            </FilterSection>

                            {facets?.durations?.length > 0 && (
                                <FilterSection title="Duration">
                                    <div className="flex flex-wrap gap-1.5">
                                        {facets.durations.map((d) => {
                                            const selected = selectedDurations.includes(d);
                                            return (
                                                <button
                                                    key={d}
                                                    onClick={() => toggleMulti("duration", d)}
                                                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${selected ? "bg-brand-500 border-brand-500 text-white" : "bg-white border-gray-200 hover:border-brand-300"}`}
                                                    data-testid={`filter-duration-${d.replace(/\s+/g, "-").toLowerCase()}`}
                                                >
                                                    {d}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </FilterSection>
                            )}

                            <FilterSection title="Highlights">
                                <Toggle
                                    label="Best sellers"
                                    checked={bestSellers}
                                    onChange={(v) => updateParams((sp) => v ? sp.set("best_sellers", "1") : sp.delete("best_sellers"))}
                                    testId="filter-best-sellers"
                                />
                            </FilterSection>
                        </aside>

                        <div className="lg:col-span-9">
                            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                                <div className="text-sm text-ink-700" data-testid="courses-result-count">
                                    {loading ? "Loading…" : `${courses.length} course${courses.length === 1 ? "" : "s"}`}
                                </div>
                                <Select value={sort} onValueChange={(v) => updateParams((sp) => sp.set("sort", v))}>
                                    <SelectTrigger className="w-56 rounded-full" data-testid="courses-sort">
                                        <SelectValue placeholder="Sort by" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {SORT_OPTIONS.map((o) => (
                                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {activeChips.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mb-5" data-testid="courses-active-chips">
                                    {activeChips.map((c, i) => (
                                        <span key={i} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-brand-50 text-brand-700 text-xs font-medium">
                                            {c.label}
                                            <button onClick={c.clear} className="hover:text-brand-900" aria-label="Remove filter"><X className="h-3 w-3" /></button>
                                        </span>
                                    ))}
                                </div>
                            )}

                            {loading ? (
                                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                                    {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="h-80 rounded-2xl bg-gray-100 animate-pulse" />)}
                                </div>
                            ) : courses.length === 0 ? (
                                <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-12 text-center">
                                    <Filter className="h-6 w-6 text-ink-400 mx-auto mb-2" />
                                    <p className="text-ink-700">No courses match your filters.</p>
                                    {activeChips.length > 0 && (
                                        <Button variant="outline" onClick={clearAll} className="mt-3 rounded-full">Clear filters</Button>
                                    )}
                                </div>
                            ) : (
                                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="courses-grid">
                                    {courses.map((c) => <CourseCard key={c.id} c={c} mode={mode || "online"} onEnroll={onEnroll} />)}
                                </div>
                            )}
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}
